import {useRef, useState} from 'react'
import styled from 'styled-components'
import ReactGA from 'react-ga4'
import {useNavigate} from 'react-router-dom'
import {Button, TextField} from '@mui/material'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import {getCurrentColors} from '../../utils/colors'
import LeftBar from '../home/components/leftBar'
import {
    CANDIDATE_FOLDERS,
    DriveFile,
    linkFolderToKb,
    slugForFolder,
    slugify,
    slugifyLive,
} from './mockData'
import {ConfirmDialog, FileTypeBadge, filesToDriveFiles} from './shared'

const COLORS = getCurrentColors()

const Page = styled.div`
    display: flex;
    height: 100vh;
    overflow: hidden !important;
`

const Main = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: #f7f9fa;
`

const Scroll = styled.div`
    flex: 1;
    overflow-y: auto;
`

const Wrap = styled.div`
    max-width: 640px;
    margin: 0 auto;
    padding: 40px 26px 60px;
`

const BackLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #8a969c;
    font-weight: 500;
    margin-bottom: 16px;
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;

    &:hover {
        color: #0c1f29;
    }

    svg {
        font-size: 15px;
    }
`

const PageHead = styled.div`
    margin-bottom: 24px;

    h1 {
        font-size: 25px;
        color: #0c1f29;
        letter-spacing: -0.03em;
        margin: 0 0 5px;
    }

    p {
        color: #5d6b72;
        font-size: 14px;
        margin: 0;
    }
`

const StepLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #8a969c;
    margin: 24px 0 10px;
`

const FolderList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const FolderRow = styled.button<{selected: boolean}>`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    padding: 13px 14px;
    border-radius: 10px;
    border: 1.5px solid
        ${({selected}) => (selected ? COLORS.primariaEscura : '#e6eaec')};
    background: ${({selected}) => (selected ? COLORS.primaria : '#ffffff')};
    cursor: pointer;
    transition: all 0.14s;

    &:hover {
        border-color: ${COLORS.primariaEscura};
    }

    svg {
        color: ${COLORS.primariaEscura};
        flex-shrink: 0;
    }
`

const FolderInfo = styled.div`
    flex: 1;
    min-width: 0;
`

const FolderName = styled.div`
    font-weight: 600;
    font-size: 14px;
    color: #0c1f29;
`

const FolderMeta = styled.div`
    font-size: 11.5px;
    color: #8a969c;
    margin-top: 2px;
`

const EmptyFolders = styled.div`
    padding: 30px 16px;
    text-align: center;
    color: #8a969c;
    font-size: 13px;
    border: 1px dashed #d6dde0;
    border-radius: 10px;
`

const Dropzone = styled.div`
    border: 2px dashed #d6dde0;
    border-radius: 12px;
    padding: 26px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        border-color: ${COLORS.primariaEscura};
        background: ${COLORS.primaria};
    }
`

const FileList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 12px;
`

const FileRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    background: #ffffff;
    border: 1px solid #e6eaec;
    border-radius: 9px;
    padding: 9px 12px;
    font-size: 13px;
    color: #33454e;
`

const FileName = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const FileRemove = styled.button`
    border: none;
    background: none;
    color: #8a969c;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    display: grid;
    place-items: center;

    &:hover {
        background: #f2f5f6;
        color: #cf3434;
    }

    svg {
        font-size: 15px;
    }
`

const Actions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 28px;
`

export default function BasesDeDadosNovo() {
    const navigate = useNavigate()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [folderId, setFolderId] = useState<string | null>(null)
    const [kbName, setKbName] = useState('')
    const [staged, setStaged] = useState<DriveFile[]>([])
    const [confirmOpen, setConfirmOpen] = useState(false)

    ReactGA.send({
        hitType: 'pageview',
        page: '/bases-de-dados/novo',
        title: 'Bases de dados - novo KB',
    })

    const folder = CANDIDATE_FOLDERS.find((f) => f.id === folderId)

    function handleSelectFolder(f: (typeof CANDIDATE_FOLDERS)[number]) {
        setFolderId(f.id)
        // Sugere o nome com base na pasta, mas o usuário pode editar livremente.
        setKbName(slugForFolder(f.name))
    }

    function handleAddFiles(fileList: FileList | null) {
        if (!fileList || fileList.length === 0) return
        setStaged((prev) => [...prev, ...filesToDriveFiles(fileList)])
    }

    function handleRemoveStaged(id: string) {
        setStaged((prev) => prev.filter((d) => d.id !== id))
    }

    function handleCreate() {
        if (!folderId || !slugify(kbName)) return
        const kb = linkFolderToKb(folderId, kbName)
        if (!kb) return
        kb.root = {
            ...kb.root,
            children: staged.map((d) => ({...d, pending: undefined})),
        }
        setConfirmOpen(false)
        navigate(`/bases-de-dados/${kb.name}`)
    }

    return (
        <Page>
            <LeftBar />
            <Main>
                <Scroll>
                    <Wrap>
                        <BackLink onClick={() => navigate('/bases-de-dados')}>
                            <ArrowBackOutlinedIcon /> Voltar para bases
                        </BackLink>

                        <PageHead>
                            <h1>Novo KB</h1>
                            <p>
                                Escolha uma pasta do Google Drive para
                                vincular a uma nova KB e envie os documentos
                                iniciais. Os índices do Pinecone são criados
                                depois, dentro da KB.
                            </p>
                        </PageHead>

                        <StepLabel>1. Pasta do Google Drive</StepLabel>
                        {CANDIDATE_FOLDERS.length === 0 ? (
                            <EmptyFolders>
                                Todas as pastas disponíveis já estão
                                vinculadas a uma KB.
                            </EmptyFolders>
                        ) : (
                            <FolderList>
                                {CANDIDATE_FOLDERS.map((f) => (
                                    <FolderRow
                                        key={f.id}
                                        selected={f.id === folderId}
                                        onClick={() => handleSelectFolder(f)}
                                    >
                                        <FolderOutlinedIcon />
                                        <FolderInfo>
                                            <FolderName>{f.name}</FolderName>
                                            <FolderMeta>
                                                {f.drivePath} · {f.fileCount}{' '}
                                                arquivos
                                            </FolderMeta>
                                        </FolderInfo>
                                        {f.id === folderId && (
                                            <CheckCircleOutlinedIcon />
                                        )}
                                    </FolderRow>
                                ))}
                            </FolderList>
                        )}

                        {folder && (
                            <TextField
                                fullWidth
                                size="small"
                                label="Nome da KB"
                                placeholder="ex.: jurisprudencia-stj"
                                helperText="Somente letras minúsculas, números e hífen — espaços e caracteres especiais são convertidos automaticamente."
                                value={kbName}
                                onChange={(e) =>
                                    setKbName(slugifyLive(e.target.value))
                                }
                                sx={{marginTop: '10px'}}
                            />
                        )}

                        <StepLabel>2. Documentos iniciais</StepLabel>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            hidden
                            onChange={(e) => {
                                handleAddFiles(e.target.files)
                                e.target.value = ''
                            }}
                        />
                        <Dropzone onClick={() => fileInputRef.current?.click()}>
                            <UploadFileOutlinedIcon
                                sx={{
                                    fontSize: 26,
                                    color: COLORS.primariaEscura,
                                }}
                            />
                            <p style={{margin: '8px 0 0', fontSize: 13}}>
                                Clique para escolher os arquivos (opcional)
                            </p>
                        </Dropzone>
                        {staged.length > 0 && (
                            <FileList>
                                {staged.map((doc) => (
                                    <FileRow key={doc.id}>
                                        <FileTypeBadge type={doc.type}>
                                            {doc.type}
                                        </FileTypeBadge>
                                        <FileName>{doc.name}</FileName>
                                        <span
                                            style={{
                                                color: '#8a969c',
                                                fontSize: 11.5,
                                            }}
                                        >
                                            {doc.size}
                                        </span>
                                        <FileRemove
                                            aria-label="Remover"
                                            onClick={() =>
                                                handleRemoveStaged(doc.id)
                                            }
                                        >
                                            <CloseOutlinedIcon />
                                        </FileRemove>
                                    </FileRow>
                                ))}
                            </FileList>
                        )}

                        <Actions>
                            <Button
                                variant="contained"
                                disabled={!folderId || !slugify(kbName)}
                                onClick={() => setConfirmOpen(true)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '9px',
                                    fontWeight: 600,
                                }}
                            >
                                Criar KB
                            </Button>
                            <Button
                                variant="text"
                                onClick={() => navigate('/bases-de-dados')}
                                sx={{textTransform: 'none'}}
                            >
                                Cancelar
                            </Button>
                        </Actions>
                    </Wrap>
                </Scroll>
            </Main>

            <ConfirmDialog
                open={confirmOpen}
                title="Confirmar criação da KB"
                uploads={staged}
                removals={[]}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleCreate}
                confirmLabel="Criar KB"
            />
        </Page>
    )
}
